import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProjectCrmAccessPanel from './ProjectCrmAccessPanel';
import { archiveProject, deleteProject, getProject, getProjects, updateProject, checkProjectSlug, getProjectMembers, addProjectMember, removeProjectMember, ProjectMember, apiClient, updateProjectSettings, AddressStructured } from '../../services/api';
import { GlassCard, GlassButton, GlassInput, ModuleHeader, EmailChipInput, AddressAutocompleteInput, BusinessHoursInput } from '../ui';
import { UserPlus, X, Crown, Loader2, ArrowLeft, Settings, Users, Archive, Trash2, AlertTriangle, Phone, ImageIcon, Bell, Link, Smile } from 'lucide-react';
import CustomDomainSection from './CustomDomainSection';
import VoiceToneSection from './VoiceToneSection';
import './ProjectSettings.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const ProjectSettings: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    const [projectName, setProjectName] = useState('');
    const [projectSlug, setProjectSlug] = useState('');
    const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
    const [slugChecking, setSlugChecking] = useState(false);
    const [projectDescription, setProjectDescription] = useState('');
    const [isArchived, setIsArchived] = useState(false);
    const [deleteConfirmName, setDeleteConfirmName] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Contact Information state
    const [clientInfo, setClientInfo] = useState<Record<string, any>>({});

    // Content Generation settings
    const [allowEmojis, setAllowEmojis] = useState<boolean>(true);
    const [savingEmojiSetting, setSavingEmojiSetting] = useState(false);
    // '' = auto-detect from business location (content_locale.py's derive_plan_locale fallback)
    const [contentLocale, setContentLocale] = useState<string>('');
    const [savingLocaleSetting, setSavingLocaleSetting] = useState(false);

    // Internal Linking settings
    const [autoInternalLinks, setAutoInternalLinks] = useState(false);
    const [autoInternalLinksReverse, setAutoInternalLinksReverse] = useState(false);
    const [savingLinkSettings, setSavingLinkSettings] = useState(false);

    // Project Access state
    const [members, setMembers] = useState<ProjectMember[]>([]);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [membersLoading, setMembersLoading] = useState(false);
    const [memberError, setMemberError] = useState<string | null>(null);

    // Landing pages (for custom domain LP picker)
    const [landingPages, setLandingPages] = useState<Array<{ landing_page_id: string; title: string; slug: string; status: string }>>([]);

    useEffect(() => {
        if (projectId) {
            loadProject();
            loadMembers();
            apiClient.get(`/landing-pages?project_id=${projectId}`)
                .then(res => setLandingPages(res.data || []))
                .catch(() => {});
        }
    }, [projectId]);

    const loadProject = async () => {
        if (!projectId) return;
        try {
            const project = await getProject(projectId);
            setProjectName(project.name);
            setProjectSlug(project.slug || '');
            setProjectDescription(project.description || '');
            setIsArchived(project.is_archived);
            const ci = project.client_info || {};
            setClientInfo(ci);
            const settings = ci.settings || {};
            setAllowEmojis(settings.allow_emojis !== false);   // absent | true -> true
            setContentLocale(settings.content_locale || '');   // absent -> '' (auto-detect)
            setAutoInternalLinks(!!settings.auto_internal_links);
            setAutoInternalLinksReverse(!!settings.auto_internal_links_reverse);
        } catch (err) {
            setError('Failed to load project details');
        }
    };

    const loadMembers = async () => {
        if (!projectId) return;
        setMembersLoading(true);
        try {
            const data = await getProjectMembers(projectId);
            setMembers(data);
        } catch {
            // Silently fail — user may not be owner/admin
        } finally {
            setMembersLoading(false);
        }
    };

    /**
     * Any toggle on this page that persists immediately via PATCH
     * /projects/{id}/settings (Path B — allow_emojis, auto_internal_links,
     * auto_internal_links_reverse) must also mirror the new value into the
     * `clientInfo` snapshot state. That snapshot is what handleUpdate's
     * "Save Changes" button PUTs back wholesale via updateProject({ client_info }),
     * and ProjectUpdate/services.update_project replaces client_info entirely
     * (setattr, not a merge) — so without this sync, clicking Save Changes
     * after flipping a toggle silently reverts it to whatever was loaded at
     * mount. See emoji-toggle-social-generation code review, Blocker 1.
     */
    const syncClientInfoSetting = (key: string, value: boolean | string) => {
        setClientInfo(prev => ({
            ...prev,
            settings: { ...(prev.settings || {}), [key]: value },
        }));
    };

    const handleAddMember = async () => {
        if (!projectId || !newMemberEmail.trim()) return;
        setIsAddingMember(true);
        setMemberError(null);
        try {
            await addProjectMember(projectId, newMemberEmail.trim());
            setNewMemberEmail('');
            await loadMembers();
        } catch (err: any) {
            setMemberError(getApiError(err, 'Failed to add member'));
        } finally {
            setIsAddingMember(false);
        }
    };

    const handleRemoveMember = async (userId: string) => {
        if (!projectId) return;
        setMemberError(null);
        try {
            await removeProjectMember(projectId, userId);
            setMembers(prev => prev.filter(m => m.user_id !== userId));
        } catch (err: any) {
            setMemberError(getApiError(err, 'Failed to remove member'));
        }
    };

    const handleUpdate = async () => {
        if (!projectId) return;
        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await updateProject(projectId, {
                name: projectName,
                slug: projectSlug || undefined,
                description: projectDescription,
                client_info: clientInfo
            });
            setSuccess('Project settings updated successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to update project'));
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * AddressAutocompleteInput's onChange fires on every keystroke, every
     * suggestion pick, AND the "Confirm address" button — but only the
     * confirm click is a deliberate, infrequent, high-intent action that
     * must not silently rely on the user finding and clicking the separate
     * "Save Changes" button below. Confirmed by direct testing: clicking
     * "Confirm address" alone did not make the branding modal's Address
     * push-checkbox selectable; only an explicit Save afterwards did.
     *
     * Detect that specific transition (confirmed_at going from unset to
     * set) and persist client_info immediately, using the same wholesale
     * PUT as "Save Changes" (client_info is replaced, not merged — see the
     * syncClientInfoSetting comment above) so nothing else on the page gets
     * clobbered. Ordinary typing and suggestion picks still rely on the
     * manual Save Changes flow, unchanged.
     */
    const handleAddressChange = async (text: string, structured: AddressStructured | null) => {
        const prevConfirmedAt = clientInfo.address_structured?.confirmed_at;
        const nextClientInfo = { ...clientInfo, address: text, address_structured: structured };
        setClientInfo(nextClientInfo);

        const justConfirmed = !!structured?.confirmed_at && !prevConfirmedAt;
        if (!justConfirmed || !projectId) return;

        try {
            await updateProject(projectId, {
                name: projectName,
                slug: projectSlug || undefined,
                description: projectDescription,
                client_info: nextClientInfo,
            });
            setError(null);
            setSuccess('Address confirmed and saved');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to save confirmed address — click Save Changes below to retry'));
        }
    };

    const handleArchiveToggle = async () => {
        if (!projectId) return;

        try {
            const newArchivedState = !isArchived;
            await archiveProject(projectId, newArchivedState);
            setIsArchived(newArchivedState);
            setSuccess(newArchivedState ? 'Project archived successfully' : 'Project restored successfully');
            setError(null);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to update archive status'));
        }
    };

    const handleDelete = async () => {
        if (!projectId) return;

        if (!showDeleteConfirm) {
            setShowDeleteConfirm(true);
            return;
        }

        setIsDeleting(true);
        setError(null);

        try {
            await deleteProject(projectId, deleteConfirmName);
            const remaining = await getProjects();
            const nextActive = remaining.find((p: any) => !p.is_archived);
            if (nextActive) {
                navigate('/dashboard', {
                    state: { refreshProjects: true, selectProjectId: nextActive.project_id },
                });
            } else {
                navigate('/onboarding');
            }
        } catch (err: any) {
            setError(getApiError(err, 'Failed to delete project'));
            setIsDeleting(false);
        }
    };

    return (
        <div className="project-settings">
            <ModuleHeader
                title="Project Settings"
                breadcrumb={['Projects', 'Settings']}
                actions={[
                    <GlassButton
                        key="back"
                        variant="secondary"
                        size="sm"
                        icon={<ArrowLeft size={16} />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </GlassButton>
                ]}
            />

            <div className="ps-content">
                {error && (
                    <GlassCard className="ps-alert ps-alert-error">
                        <AlertTriangle size={16} />
                        <span>{error}</span>
                    </GlassCard>
                )}
                {success && (
                    <GlassCard className="ps-alert ps-alert-success">
                        <span>{success}</span>
                    </GlassCard>
                )}

                {/* General Settings */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Settings size={20} />
                        <h3>General Settings</h3>
                    </div>
                    <div className="ps-form-group">
                        <GlassInput
                            label="Project Name"
                            value={projectName}
                            onChange={setProjectName}
                            placeholder="Enter project name"
                        />
                    </div>
                    <div className="ps-form-group">
                        <label className="ps-label">URL Slug</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                className="ps-input"
                                style={{ flex: 1, padding: '8px 12px', background: 'var(--panel-strong)', border: `1px solid ${slugAvailable === false ? '#ef4444' : 'var(--stroke)'}`, borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.875rem', outline: 'none' }}
                                value={projectSlug}
                                onChange={async e => {
                                    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
                                    setProjectSlug(val);
                                    setSlugAvailable(null);
                                    if (val.length >= 2) {
                                        setSlugChecking(true);
                                        try {
                                            const res = await checkProjectSlug(val, projectId);
                                            setSlugAvailable(res.available);
                                        } catch { setSlugAvailable(null); }
                                        finally { setSlugChecking(false); }
                                    }
                                }}
                                placeholder="my-project"
                            />
                            {slugChecking && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Checking…</span>}
                            {!slugChecking && slugAvailable === true && <span style={{ fontSize: 12, color: '#22c55e' }}>✓ Available</span>}
                            {!slugChecking && slugAvailable === false && <span style={{ fontSize: 12, color: '#ef4444' }}>✗ Taken</span>}
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                            Landing pages will be served at <code style={{ background: 'var(--panel-strong)', padding: '1px 4px', borderRadius: 3 }}>/pages/{projectSlug || 'your-slug'}/page-name</code>.
                            Changing this will redirect old URLs permanently.
                        </p>
                    </div>
                    <div className="ps-form-group">
                        <label className="ps-label">Description</label>
                        <textarea
                            value={projectDescription}
                            onChange={(e) => setProjectDescription(e.target.value)}
                            placeholder="Enter project description"
                            rows={3}
                            className="ps-textarea"
                        />
                    </div>
                    <GlassButton
                        onClick={handleUpdate}
                        disabled={isSaving || !projectName.trim()}
                        loading={isSaving}
                    >
                        Save Changes
                    </GlassButton>
                </GlassCard>

                {/* Contact Information */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Phone size={20} />
                        <h3>Contact Information</h3>
                    </div>
                    <p className="ps-description">
                        These details are used in generated landing pages (footer contact section and office hours). A confirmed address and your structured business hours can be pushed to your connected Facebook Page.
                    </p>
                    <div className="ps-form-group">
                        <GlassInput
                            label="Phone Number"
                            value={clientInfo.phone || ''}
                            onChange={(val) => setClientInfo(prev => ({ ...prev, phone: val }))}
                            placeholder="e.g. (03) 9000 1234"
                        />
                        <p className="ps-field-hint">
                            A local number is fine (e.g. "(03) 9000 1234") — it's normalized automatically
                            for the connected Facebook Page based on this project's business location. If a
                            push still fails, try including the country code (e.g. "+61 3 9000 1234").
                        </p>
                    </div>
                    <div className="ps-form-group">
                        <GlassInput
                            label="Email Address"
                            value={clientInfo.email || ''}
                            onChange={(val) => setClientInfo(prev => ({ ...prev, email: val }))}
                            placeholder="e.g. info@example.com"
                        />
                    </div>
                    <div className="ps-form-group">
                        <GlassInput
                            label="Website"
                            type="url"
                            value={clientInfo.website || ''}
                            onChange={(val) => setClientInfo(prev => ({ ...prev, website: val }))}
                            onBlur={() => {
                                setClientInfo(prev => {
                                    const raw = (prev.website || '').trim();
                                    if (!raw || /^https?:\/\//i.test(raw)) {
                                        return raw === prev.website ? prev : { ...prev, website: raw };
                                    }
                                    return { ...prev, website: `https://${raw}` };
                                });
                            }}
                            placeholder="https://example.com"
                        />
                    </div>
                    <div className="ps-form-group">
                        <label className="ps-label">Office Hours (shown on landing pages)</label>
                        <textarea
                            value={clientInfo.office_hours || ''}
                            onChange={(e) => setClientInfo(prev => ({ ...prev, office_hours: e.target.value }))}
                            placeholder={"Mon - Fri: 9am - 6pm\nSat - Sun: Closed"}
                            rows={2}
                            className="ps-textarea"
                        />
                    </div>
                    <div className="ps-form-group">
                        <label className="ps-label">Business Hours (for Facebook)</label>
                        <BusinessHoursInput
                            value={clientInfo.office_hours_structured || null}
                            onChange={(next) => setClientInfo(prev => ({ ...prev, office_hours_structured: next }))}
                        />
                    </div>
                    <AddressAutocompleteInput
                        value={clientInfo.address || ''}
                        structured={clientInfo.address_structured || null}
                        onChange={handleAddressChange}
                    />
                </GlassCard>

                {/* Carousel & Image Settings */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <ImageIcon size={20} />
                        <h3>Carousel & Image Settings</h3>
                    </div>
                    <p className="ps-description">
                        Configure default image generation style and logo watermarking for carousel content.
                    </p>
                    <div className="ps-form-group">
                        <label className="ps-label">Image Style</label>
                        <select
                            value={clientInfo.carousel_image_style || ''}
                            onChange={(e) => setClientInfo(prev => ({ ...prev, carousel_image_style: e.target.value }))}
                            className="ps-select"
                        >
                            <option value="">None</option>
                            <option value="photographic">Photographic</option>
                            <option value="illustration">Illustration</option>
                            <option value="flat_design">Flat Design</option>
                            <option value="abstract">Abstract</option>
                            <option value="minimal_icons">Minimal Icons</option>
                            <option value="3d_render">3D Render</option>
                        </select>
                    </div>
                    <div className="ps-form-group">
                        <div className="ps-archive-toggle">
                            <label className="ps-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!!clientInfo.carousel_logo_watermark}
                                    onChange={(e) => setClientInfo(prev => ({ ...prev, carousel_logo_watermark: e.target.checked }))}
                                />
                                <span className="ps-toggle-slider"></span>
                            </label>
                            <span className="ps-toggle-label">Logo Watermark</span>
                        </div>
                    </div>
                    {clientInfo.carousel_logo_watermark && (
                        <div className="ps-form-group">
                            <label className="ps-label">Logo Variant</label>
                            <select
                                value={clientInfo.carousel_logo_variant || 'primary'}
                                onChange={(e) => setClientInfo(prev => ({ ...prev, carousel_logo_variant: e.target.value }))}
                                className="ps-select"
                            >
                                <option value="primary">Primary</option>
                                <option value="dark_bg">Dark Background</option>
                                <option value="monochrome_white">Monochrome White</option>
                                <option value="monochrome_black">Monochrome Black</option>
                            </select>
                        </div>
                    )}
                </GlassCard>

                {/* Lead Notifications */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Bell size={20} />
                        <h3>Lead Notifications</h3>
                    </div>
                    <p className="ps-description">
                        Default recipients to email when a lead is captured (form submission or landing page).
                        Lead capture forms can override these defaults individually.
                    </p>
                    <div className="ps-form-group">
                        <EmailChipInput
                            label="Default recipients"
                            value={Array.isArray(clientInfo.lead_notification_emails) ? clientInfo.lead_notification_emails : []}
                            onChange={(next) => setClientInfo(prev => ({ ...prev, lead_notification_emails: next }))}
                            placeholder="alice@example.com, then Enter"
                            helperText="These addresses receive every new lead for this project, unless a form sets its own list."
                        />
                    </div>
                    <div className="ps-form-group">
                        <div className="ps-archive-toggle">
                            <label className="ps-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!!clientInfo.lead_notification_digest}
                                    onChange={(e) => setClientInfo(prev => ({ ...prev, lead_notification_digest: e.target.checked }))}
                                />
                                <span className="ps-toggle-slider"></span>
                            </label>
                            <span className="ps-toggle-label">Batch into a daily digest (08:00 UTC)</span>
                        </div>
                    </div>
                </GlassCard>

                {/* Custom Domain */}
                {projectId && (
                    <CustomDomainSection projectId={projectId} landingPages={landingPages} />
                )}

                {/* Project Access */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Users size={20} />
                        <h3>Project Access</h3>
                    </div>
                    <p className="ps-description">
                        Manage which team members can access this project. The project owner always has access.
                    </p>

                    {memberError && (
                        <div className="ps-inline-error">
                            <AlertTriangle size={14} />
                            <span>{memberError}</span>
                        </div>
                    )}

                    <div className="ps-add-member-row">
                        <GlassInput
                            type="email"
                            value={newMemberEmail}
                            onChange={setNewMemberEmail}
                            placeholder="Enter email address"
                        />
                        <GlassButton
                            onClick={handleAddMember}
                            disabled={isAddingMember || !newMemberEmail.trim()}
                            loading={isAddingMember}
                            icon={<UserPlus size={16} />}
                        >
                            Add
                        </GlassButton>
                    </div>

                    {membersLoading ? (
                        <div className="ps-loading">
                            <Loader2 size={16} className="ps-spin" />
                            <span>Loading members...</span>
                        </div>
                    ) : members.length > 0 ? (
                        <div className="ps-members-list">
                            {members.map(member => (
                                <div key={member.user_id} className="ps-member-row">
                                    <div className="ps-member-avatar">
                                        {(member.full_name || member.email).charAt(0).toUpperCase()}
                                    </div>
                                    <div className="ps-member-info">
                                        <span className="ps-member-name">
                                            {member.full_name || member.email}
                                            {member.is_owner && (
                                                <span className="ps-owner-badge">
                                                    <Crown size={12} /> Owner
                                                </span>
                                            )}
                                        </span>
                                        <span className="ps-member-email">{member.email}</span>
                                    </div>
                                    {!member.is_owner && (
                                        <GlassButton
                                            variant="ghost"
                                            size="sm"
                                            icon={<X size={16} />}
                                            onClick={() => handleRemoveMember(member.user_id)}
                                            title="Remove access"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="ps-empty-state">No additional members. Only the owner has access.</p>
                    )}

                    {/* crm-per-project-scoping: who can open this project's CRM records */}
                    {projectId && <ProjectCrmAccessPanel projectId={projectId} />}
                </GlassCard>

                {/* Archive Project */}
                {/* Content Generation */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Smile size={20} />
                        <h3>Content Generation</h3>
                    </div>
                    <div className="ps-archive-toggle" style={{ marginBottom: 12 }}>
                        <label className="ps-toggle-switch">
                            <input
                                type="checkbox"
                                checked={allowEmojis}
                                disabled={savingEmojiSetting}
                                onChange={async (e) => {
                                    if (!projectId) return;
                                    const nextValue = e.target.checked;
                                    setAllowEmojis(nextValue);
                                    setSavingEmojiSetting(true);
                                    try {
                                        await updateProjectSettings(projectId, { allow_emojis: nextValue });
                                        syncClientInfoSetting('allow_emojis', nextValue);
                                    } catch (err) {
                                        setAllowEmojis(!nextValue);
                                        setError(getApiError(err, 'Failed to update emoji setting'));
                                    } finally {
                                        setSavingEmojiSetting(false);
                                    }
                                }}
                            />
                            <span className="ps-toggle-slider"></span>
                        </label>
                        <span className="ps-toggle-label">
                            Allow emojis in AI-generated content
                        </span>
                    </div>
                    <p className="ps-description">
                        When off, AIME will not use emojis in social posts, campaign copy, comment drafts or
                        newsletter subject lines for this project.
                    </p>

                    <div className="ps-form-group" style={{ marginTop: 16 }}>
                        <label className="ps-label" htmlFor="content-locale-select">
                            Content spelling &amp; language
                        </label>
                        <select
                            id="content-locale-select"
                            className="ps-select"
                            value={contentLocale}
                            disabled={savingLocaleSetting}
                            onChange={async (e) => {
                                if (!projectId) return;
                                const nextValue = e.target.value;
                                const prevValue = contentLocale;
                                setContentLocale(nextValue);
                                setSavingLocaleSetting(true);
                                try {
                                    await updateProjectSettings(projectId, { content_locale: nextValue });
                                    syncClientInfoSetting('content_locale', nextValue);
                                } catch (err) {
                                    setContentLocale(prevValue);
                                    setError(getApiError(err, 'Failed to update content locale'));
                                } finally {
                                    setSavingLocaleSetting(false);
                                }
                            }}
                        >
                            <option value="">Auto-detect from business location</option>
                            <option value="en-AU">Australian English</option>
                            <option value="en-GB">British English</option>
                            <option value="en-US">US English</option>
                            <option value="en-NZ">New Zealand English</option>
                            <option value="en-CA">Canadian English</option>
                            <option value="en-SG">Singapore English</option>
                        </select>
                        <p className="ps-description">
                            Controls spelling and word choice (e.g. colour vs color) in AI-generated
                            articles and ad copy for this project. "Auto-detect" uses the business
                            location on file and defaults to US English when that's unknown.
                        </p>
                    </div>
                </GlassCard>

                {/* Voice & Tone */}
                {projectId && <VoiceToneSection projectId={projectId} />}

                {/* Internal Linking */}
                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Link size={20} />
                        <h3>Internal Linking</h3>
                    </div>
                    <p className="ps-description">
                        Control how AIME automatically suggests and applies internal links between articles in this project.
                    </p>
                    <div className="ps-archive-toggle" style={{ marginBottom: 12 }}>
                        <label className="ps-toggle-switch">
                            <input
                                type="checkbox"
                                checked={autoInternalLinks}
                                disabled={savingLinkSettings}
                                onChange={async (e) => {
                                    if (!projectId) return;
                                    setAutoInternalLinks(e.target.checked);
                                    setSavingLinkSettings(true);
                                    try {
                                        await updateProjectSettings(projectId, { auto_internal_links: e.target.checked });
                                        syncClientInfoSetting('auto_internal_links', e.target.checked);
                                    } catch {
                                        setAutoInternalLinks(!e.target.checked);
                                    } finally {
                                        setSavingLinkSettings(false);
                                    }
                                }}
                            />
                            <span className="ps-toggle-slider"></span>
                        </label>
                        <span className="ps-toggle-label">
                            Auto-insert internal links on publish (max 5 per article)
                        </span>
                    </div>
                    <div className="ps-archive-toggle">
                        <label className="ps-toggle-switch">
                            <input
                                type="checkbox"
                                checked={autoInternalLinksReverse}
                                disabled={savingLinkSettings}
                                onChange={async (e) => {
                                    if (!projectId) return;
                                    setAutoInternalLinksReverse(e.target.checked);
                                    setSavingLinkSettings(true);
                                    try {
                                        await updateProjectSettings(projectId, { auto_internal_links_reverse: e.target.checked });
                                        syncClientInfoSetting('auto_internal_links_reverse', e.target.checked);
                                    } catch {
                                        setAutoInternalLinksReverse(!e.target.checked);
                                    } finally {
                                        setSavingLinkSettings(false);
                                    }
                                }}
                            />
                            <span className="ps-toggle-slider"></span>
                        </label>
                        <span className="ps-toggle-label">
                            Auto-edit existing articles when a new one publishes (WordPress only)
                        </span>
                    </div>
                </GlassCard>

                <GlassCard padding="lg">
                    <div className="ps-section-header">
                        <Archive size={20} />
                        <h3>Archive Project</h3>
                    </div>
                    <p className="ps-description">
                        Archive this project to remove it from the main workflow.
                        You can restore it at any time from the dashboard.
                    </p>
                    <div className="ps-archive-toggle">
                        <label className="ps-toggle-switch">
                            <input
                                type="checkbox"
                                checked={isArchived}
                                onChange={handleArchiveToggle}
                            />
                            <span className="ps-toggle-slider"></span>
                        </label>
                        <span className="ps-toggle-label">
                            {isArchived ? 'Project is archived' : 'Project is active'}
                        </span>
                    </div>
                </GlassCard>

                {/* Danger Zone */}
                <GlassCard padding="lg" className="ps-danger-zone">
                    <div className="ps-section-header ps-danger-header">
                        <Trash2 size={20} />
                        <h3>Delete Project</h3>
                    </div>
                    <p className="ps-description ps-danger-text">
                        This action is <strong>irreversible</strong>. All project data,
                        conversations, tasks, and content will be permanently deleted.
                    </p>

                    {!showDeleteConfirm ? (
                        <GlassButton
                            variant="danger"
                            onClick={handleDelete}
                            icon={<Trash2 size={16} />}
                        >
                            Delete Project
                        </GlassButton>
                    ) : (
                        <div className="ps-delete-confirmation">
                            <p className="ps-confirm-prompt">
                                Type the project name to confirm deletion:
                            </p>
                            <GlassInput
                                value={deleteConfirmName}
                                onChange={setDeleteConfirmName}
                                placeholder="Enter project name"
                            />
                            <div className="ps-confirm-actions">
                                <GlassButton
                                    variant="danger"
                                    onClick={handleDelete}
                                    disabled={isDeleting || deleteConfirmName.trim() === ''}
                                    loading={isDeleting}
                                    icon={<Trash2 size={16} />}
                                >
                                    Confirm Delete
                                </GlassButton>
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => {
                                        setShowDeleteConfirm(false);
                                        setDeleteConfirmName('');
                                    }}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </GlassButton>
                            </div>
                        </div>
                    )}
                </GlassCard>
            </div>
        </div>
    );
};

export default ProjectSettings;
